// src/modules/dashboard/pages/FitHub/index.js
import React, { useState, useEffect, useCallback } from 'react';
import { useWarmupTimer } from '../../../../shared/hooks/useWarmupTimer';
import { Container } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useService } from '../../../../shared/hooks/useService';
import { useAuth } from '../../../../shared/hooks/useAuth';
import { parseLocalDate } from '../../../../shared/utils/dateUtils';
import CompetitionsOverview from '../../components/CompetitionsOverview';
import RecentActivities from '../../components/RecentActivities';
import Loading from '../../../../shared/components/Loading';
import ErrorState from '../../../../shared/components/ErrorState';
import './FitHub.css';

const FitHub = () => {
    const [dashboardData, setDashboardData] = useState(null);
    const [allCompetitions, setAllCompetitions] = useState({
        active: [],
        upcoming: [],
        concluded: []
    });
    const [recentFeed, setRecentFeed] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const { isWarmup } = useWarmupTimer(isLoading);
    const [error, setError] = useState(null);
    const service = useService();
    const { user } = useAuth();
    // eslint-disable-next-line no-unused-vars
    const navigate = useNavigate();

    const loadInitialData = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            // Load dashboard data, all competitions, and recent feed in parallel
            const [dashboardResponse, competitionsResponse, feedResponse] = await Promise.all([
                service.getFitHubData(),
                service.getAllCompetitions(),
                service.getActivities(undefined, 5)
            ]);

            setDashboardData(dashboardResponse);

            // Set recent feed with user names
            const activitiesWithUserGivenName = feedResponse.activities.map(activity => ({
                ...activity,
                userGivenName: `${activity.firstName} ${activity.lastName}`
            }));
            setRecentFeed(activitiesWithUserGivenName);

            // Separate competitions by state
            const active = [];
            const upcoming = [];
            const concluded = [];

            competitionsResponse.forEach(comp => {
                const enrichedComp = {
                    ...comp,
                    // Try to find user-specific data from dashboard if available
                    ...(dashboardResponse.activeCompetitions?.find(
                        ac => ac.competitionId === comp.competitionId
                    ) || {})
                };

                if (comp.state === 'active') {
                    active.push(enrichedComp);
                } else if (comp.state === 'upcoming') {
                    upcoming.push(enrichedComp);
                } else if (comp.state === 'concluded') {
                    concluded.push(enrichedComp);
                }
            });

            // Sort concluded by end date (most recent first)
            concluded.sort((a, b) => parseLocalDate(b.endDate) - parseLocalDate(a.endDate));

            setAllCompetitions({ active, upcoming, concluded });
        } catch (err) {
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    }, [service]);

    useEffect(() => {
        loadInitialData();
    }, [loadInitialData]);

    const handleRetry = () => {
        loadInitialData();
    };

    if (isLoading) return <Loading isWarmup={isWarmup} />;
    if (error) return <ErrorState message={error} onRetry={handleRetry} />;
    if (!dashboardData) return <ErrorState message="No data available" onRetry={handleRetry} />;

    return (
        <Container className="fithub-container">
            {/* Main Content */}
            <div className="fithub-layout">
                {/* Main Column - Competitions Overview */}
                <div className="fithub-main">
                    <CompetitionsOverview
                        activeCompetitions={allCompetitions.active}
                        upcomingCompetitions={allCompetitions.upcoming}
                        concludedCompetitions={allCompetitions.concluded}
                    />
                </div>

                {/* Sidebar - Recent Activities */}
                <div className="fithub-sidebar">
                    <RecentActivities
                        activities={recentFeed}
                        title="Recent Activities"
                        maxItems={3}
                        showAddButton={false}
                        viewAllLink="/activities"
                    />
                    <RecentActivities
                        activities={dashboardData.recentActivities}
                        title="My Recent Activities"
                        maxItems={3}
                        showAddButton={true}
                        viewAllLink={`/activities/${user?.Username}`}
                    />
                </div>
            </div>
        </Container>
    );
};

export default FitHub;
